/**
 * Bouwt de brede onderzoeksdataset (rij per respondent × organisatie ×
 * meetmoment, kolom per item) uit de platte cross-tenant rijen van de
 * msiResearchData Cloud Function.
 *
 * - Item-alignment over organisaties heen: msiId + genormaliseerde rijtitel
 *   (tenants hebben eigen MatrixQuestionRows-kopieën met eigen IDs — nooit
 *   op RowID matchen). AI-CONTEXT: See docs/pitfalls.md#ui-msi-matrix-rowdefs-collapse
 * - Negatieve items worden gespiegeld binnen de veldschaal, consistent met
 *   useMatrixData: alleen de string 'negative' telt.
 * - Respondenten worden gepseudonimiseerd (stabiele labels R1…Rn); de rauwe
 *   Persona-/FormID-keys komen niet in de output voor.
 *
 * Pure function — no Firebase imports.
 */

import mirrorScore from './mirrorScore'

/**
 * @typedef {object} ResearchDataRow - Eén CF-rij (één datapunt)
 * @property {string} org - CompagnyID van de organisatie
 * @property {string} orgName - Organisatienaam (zichtbaar voor onderzoekers)
 * @property {string} respondent - Rauwe respondent-key (Persona of FormID) — verlaat deze helper niet
 * @property {string} momentId - MeasureMoment ID
 * @property {string} momentLabel - Meetmoment als m1, m2, m3 (volgorde binnen het eigen onderzoek); '' = onbekend moment
 * @property {string} msiId - Meetstandaard effect-ID
 * @property {string} effectName - Effectnaam
 * @property {string} itemTitle - Rijtitel (item)
 * @property {number|string} score - Ruwe score
 * @property {string|boolean|undefined} posNeg - Alleen string 'negative' spiegelt
 * @property {number} lowerScale - Onderkant veldschaal (ReachStart)
 * @property {number} upperScale - Bovenkant veldschaal (ReachEnd)
 */

/**
 * @typedef {object} ResearchDatasetColumn
 * @property {string} key - Stabiele kolomsleutel (msiId + genormaliseerde titel)
 * @property {string} msiId - Meetstandaard effect-ID
 * @property {string} effectName - Effectnaam
 * @property {string} itemTitle - Weergavetitel (eerste aangetroffen spelling)
 */

/**
 * @typedef {object} ResearchDataset
 * @property {ResearchDatasetColumn[]} columns - Kolommen, gesorteerd op effect + item
 * @property {Array<{id: string, respondentLabel: string, orgName: string, momentLabel: string, scores: Object<string, number>}>} rows - Brede rijen
 * @property {Object<string, string[]>} itemKeysByEffect - Kolomsleutels per msiId (voor Cronbach's alpha)
 */

const normalizeTitle = (title) =>
    String(title || '').trim().toLowerCase().replace(/\s+/g, ' ')

const toScore = (value) => {
    if (value === '' || value == null) return null
    const num = Number(value)
    return Number.isFinite(num) ? num : null
}

/**
 * @param {ResearchDataRow[]} inputRows - Platte datapunten van de Cloud Function
 * @returns {ResearchDataset}
 */
const buildMsiResearchDataset = (inputRows) => {
    const list = Array.isArray(inputRows) ? inputRows : []

    /** @type {Object<string, ResearchDatasetColumn>} */
    const columnsByKey = {}
    // rowKey (org|respondent|moment) → { orgName, momentLabel, sums: {colKey: {sum, count}} }
    /** @type {Object<string, {org: string, respondent: string, orgName: string, momentLabel: string, sums: Object<string, {sum: number, count: number}>}>} */
    const rowsByKey = {}

    list.forEach((row) => {
        const score = toScore(row?.score)
        if (score === null) return

        const columnKey = `${row.msiId}::${normalizeTitle(row.itemTitle)}`
        if (!columnsByKey[columnKey]) {
            columnsByKey[columnKey] = {
                key: columnKey,
                msiId: String(row.msiId),
                effectName: row.effectName || '',
                itemTitle: String(row.itemTitle || '').trim(),
            }
        }

        // Spiegel negatieve items zodat hoger = beter, zelfde formule als useMatrixData
        const adjusted = mirrorScore(
            score,
            { lowerScale: row.lowerScale, upperScale: row.upperScale },
            row.posNeg === 'negative'
        )

        const rowKey = `${row.org}|${row.respondent}|${row.momentId}`
        const bucket = (rowsByKey[rowKey] ||= {
            org: row.org,
            respondent: row.respondent,
            orgName: row.orgName || '',
            momentId: row.momentId || '',
            momentLabel: row.momentLabel || '',
            sums: {},
        })
        const cell = (bucket.sums[columnKey] ||= { sum: 0, count: 0 })
        cell.sum += adjusted
        cell.count += 1
    })

    // Stabiele pseudoniemen: sorteer de unieke org|respondent-keys en nummer ze
    const respondentKeys = [...new Set(
        Object.values(rowsByKey).map((bucket) => `${bucket.org}|${bucket.respondent}`)
    )].sort()
    const labelByRespondent = Object.fromEntries(
        respondentKeys.map((key, index) => [key, `R${index + 1}`])
    )

    const columns = Object.values(columnsByKey).sort((a, b) =>
        a.effectName.localeCompare(b.effectName) || a.itemTitle.localeCompare(b.itemTitle)
    )

    const rows = Object.keys(rowsByKey).sort().map((rowKey) => {
        const bucket = rowsByKey[rowKey]
        const respondentLabel = labelByRespondent[`${bucket.org}|${bucket.respondent}`]
        /** @type {Object<string, number>} */
        const scores = {}
        Object.entries(bucket.sums).forEach(([columnKey, cell]) => {
            scores[columnKey] = cell.sum / cell.count
        })
        return {
            // momentId (niet momentLabel) — momenten uit verschillende
            // onderzoeken heten allebei m1 en zouden dezelfde React-key geven
            id: `${respondentLabel}|${bucket.org}|${bucket.momentId}`,
            respondentLabel,
            orgName: bucket.orgName,
            momentLabel: bucket.momentLabel,
            scores,
        }
    })

    /** @type {Object<string, string[]>} */
    const itemKeysByEffect = {}
    columns.forEach((column) => {
        (itemKeysByEffect[column.msiId] ||= []).push(column.key)
    })

    return { columns, rows, itemKeysByEffect }
}

/**
 * Respondent × item scorematrix voor één effect (input voor cronbachAlpha).
 * Ontbrekende items worden null (cronbachAlpha doet listwise deletion).
 *
 * @param {ResearchDataset} dataset - Output van buildMsiResearchDataset
 * @param {string} msiId - Meetstandaard effect-ID
 * @returns {Array<Array<number|null>>}
 */
export const effectScoreMatrix = (dataset, msiId) => {
    const itemKeys = dataset?.itemKeysByEffect?.[msiId]
    if (!itemKeys || itemKeys.length === 0) return []

    return (dataset.rows || [])
        .map((row) => itemKeys.map((key) => (Number.isFinite(row.scores[key]) ? row.scores[key] : null)))
        .filter((row) => row.some((value) => value !== null))
}

export default buildMsiResearchDataset
